import { useState } from 'react';
import { Chess } from 'chess.js';
import { proxy, useSnapshot } from 'valtio';

import { FEN_STARTING_POSITION } from '@libs/chess/constants';
import { IPgnMove, Pgn } from '@libs/chess/pgn';
import { isFen } from '@libs/chess/validateFen';
import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';
import { openTextFile } from '@libs/openFile';
import { IconUpload } from '@ui/icons-buttons';

import { Button, TextArea } from '@features/game/ui/PopupControls';
import stylesContols from '@features/game/ui/PopupControls/PopupControls.module.css';

import styles from './ImportModal.module.css';

type Props = {
  className?: string;
  initialValue?: string;
  readOnly?: boolean;
  showFileUpload?: boolean;
  onClose?: () => void;
  onApply?: (data: IImportData) => void;
};

export type IImportData = {
  fen: string;
  pgn?: Pgn<IPgnMove>;
  multi?: Pgn<IPgnMove>[];
};

export function ImportModal({
  className,
  initialValue = FEN_STARTING_POSITION,
  readOnly,
  showFileUpload,
  onClose,
  onApply,
}: Props) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  useOnEscape(onClose);

  const [store] = useState(() => new Store(initialValue));
  store.keepUpToDate(onApply);
  const snap = useSnapshot(store.state);

  return (
    <div className={cx(styles.popup, stylesContols.container, className)} ref={ref}>
      <TextArea
        key={snap.fileLoadCount}
        className={styles.textarea}
        initialValue={snap.fileLoadCount > 0 ? snap.text : initialValue}
        onChange={store.onTextChange}
        autoFocus
      />

      <div className={styles.buttons}>
        {!readOnly && (
          <Button onClick={store.onApply} disabled={snap.disabled}>
            {snap.error ? <span className={styles.error}>Ошибка! {snap.error}</span> : 'Применить'}
          </Button>
        )}
        <Button onClick={store.onCopy}>{snap.copied ? 'Скопировано!' : 'Копировать'}</Button>
        {showFileUpload && (
          <Button onClick={store.onLoadFile}>
            <IconUpload className={styles.icon} /> Загрузить из файла
          </Button>
        )}
        <br />
        <Button ghost onClick={onClose}>
          Закрыть
        </Button>
      </div>
    </div>
  );
}

class State {
  constructor(initialText: string) {
    this.initialText = initialText;
    this.text = initialText;
  }

  initialText: string;
  text: string;
  error?: string;
  copied = false;
  fileLoadCount = 0;

  get disabled() {
    const trimmed = this.text.trim();
    return !trimmed || trimmed === this.initialText.trim();
  }
}

class Store {
  constructor(
    initialText: string,
    private _onApply?: (data: IImportData) => void,
  ) {
    this.state = proxy(new State(initialText));
  }

  state: State;

  keepUpToDate(_onApply?: (data: IImportData) => void) {
    this._onApply = _onApply;
  }

  onTextChange = (text: string) => {
    this.state.text = text;
    this.state.error = undefined;
  };

  onCopy = () => {
    navigator.clipboard.writeText(this.state.text.trim());
    this.state.copied = true;
    setTimeout(() => {
      this.state.copied = false;
    }, 2000);
  };

  onLoadFile = () => {
    openTextFile((text: string) => {
      if (!this.handleTextImport(text)) {
        this.state.text = text;
        this.state.fileLoadCount++;
      }
    }, '.pgn,.txt');
  };

  onApply = () => {
    this.handleTextImport(this.state.text);
  };

  private handleTextImport(raw: string): boolean {
    this.state.error = undefined;
    const text = raw.trim();
    if (isFen(text)) {
      try {
        new Chess(text, { enable960: true, skipValidation: true });
        this._onApply?.({ fen: text });
        return true;
      } catch {
        this.state.error = 'Неверный FEN';
      }
    } else {
      try {
        const pgns = Pgn.parseMany(text);
        if (pgns.length) {
          const pgn = pgns[0]!;
          this._onApply?.({
            fen: pgn.fen ?? FEN_STARTING_POSITION,
            pgn,
            multi: pgns.length > 1 ? pgns : undefined,
          });
          return true;
        } else {
          this.state.error = 'Неверный формат';
        }
      } catch (e) {
        console.error(e);
        this.state.error = 'Неверный формат';
      }
    }

    return false;
  }
}
